import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject, takeUntil, tap, catchError, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AssetMappingConfig, AssetFieldConfig, getFieldConfig, extractNameFromFieldConfigKey } from '../../../core/models/asset-config.model';
import { MAP_FIELD_SCOPES, MapFieldScope } from '../../../core/config/config.constants';
import { LoggerService } from '../../../core/services/logger.service';
import { IotAssetsService } from '../../../core/services/iotAssets.service';
import { AppStateInterface } from '../../../appState.interface';
import { WarningModalComponent } from '../warning-modal/warning-modal.component';
import { faSearch, faTimes as faClear, faWarning, faInfoCircle, faGripVertical, faSignal, faCogs, faDesktop, faBuilding, faWrench, faQuestionCircle, faToggleOn, faComment, faColumns, faListUl, faSort, faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-map-configuration',
  templateUrl: './map-configuration.component.html',
  styleUrls: ['./map-configuration.component.scss']
})
export class MapConfigurationComponent implements OnInit, OnDestroy, OnChanges {
  private static readonly MAX_POPUP_FIELDS = 10;
  private destroy$ = new Subject<void>();

  @Input() mappingConfig: AssetMappingConfig | null = null;
  @Input() selectedAudience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER';

  // Catalog configuration data
  catalogConfig: { [key: string]: any } | null = null;

  // Icons
  faSearch = faSearch;
  faClear = faClear;
  faWarning = faWarning;
  faInfoCircle = faInfoCircle;
  faGripVertical = faGripVertical;
  faSignal = faSignal;
  faCogs = faCogs;
  faDesktop = faDesktop;
  faBuilding = faBuilding;
  faWrench = faWrench;
  faQuestionCircle = faQuestionCircle;
  faToggleOn = faToggleOn;
  faComment = faComment;
  faColumns = faColumns;
  faListUl = faListUl;
  faSort = faSort;
  faChevronRight = faChevronRight;
  faChevronDown = faChevronDown;

  // Map fields data
  mapFields: { [scope: string]: Array<{
    key: string;
    config: AssetFieldConfig;
    visible: boolean;
  }> } = {};

  filteredMapFields: { [scope: string]: Array<{
    key: string;
    config: AssetFieldConfig;
    visible: boolean;
    highlighted?: boolean;
  }> } = {};

  // States
  isLoadingConfig = false;
  configError: string | null = null;
  expandedGroups: { [scope: string]: boolean } = {};
  searchQuery: string = '';
  selectedModel: string = '';
  availableModels: string[] = [];
  scopeOrder: string[] = [];

  constructor(
    private translateService: TranslateService,
    private logger: LoggerService,
    private dialog: MatDialog,
    private iotAssetsService: IotAssetsService,
    private store: Store<AppStateInterface>
  ) {}

  ngOnInit(): void {
    this.logger.debug('map-configuration', 'Componente inizializzato');
    // Gli accordion partono tutti chiusi di default
    
    // Carica il catalog_config iniziale
    this.loadCatalogConfiguration();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Ricarica catalog se cambia l'audience
    /* if (changes['selectedAudience'] && !changes['selectedAudience'].firstChange) {
      this.logger.debug('map-configuration', 'Audience cambiata, ricarico catalog', this.selectedAudience);
      this.loadCatalogConfiguration();
    } */
    
    // Se cambia mappingConfig e il catalog è già caricato, ricostruisci la lista
    if (changes['mappingConfig'] && this.mappingConfig) {
      this.logger.debug('map-configuration', 'MappingConfig aggiornato', this.mappingConfig);
      
      // Solo se il catalog è già stato caricato, altrimenti aspetta che loadCatalogConfiguration() lo faccia
      if (this.catalogConfig !== null) {
        this.buildFieldsList();
      }
    }
  }

  /**
   * Carica la configurazione del catalog per arricchire i dati dei campi
   */
  loadCatalogConfiguration(): void {
    this.logger.debug('map-configuration', 'Caricamento catalog_config per audience:', this.selectedAudience);

    this.iotAssetsService.loadCatalogConfiguration(this.selectedAudience)
      .pipe(
        takeUntil(this.destroy$),
        tap(result => {
          this.catalogConfig = result.data;
          this.logger.debug('map-configuration', 'Catalog_config caricato', this.catalogConfig);
          
          // Ricostruisci la lista campi con i dati del catalog
          if (this.mappingConfig) {
            this.buildFieldsList();
          }
        }),
        catchError(error => {
          this.logger.error('map-configuration', 'Errore caricamento catalog_config', error);
          this.catalogConfig = null;
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * Ricarica tutti i dati del componente (chiamato dopo un salvataggio)
   */
  public reloadData(): void {
    this.logger.info('map-configuration', 'Ricaricamento dati componente...');
    
    // Ricarica il catalog configuration
    this.loadCatalogConfiguration();
    
    // Se c'è già mappingConfig, ricostruisci la lista
    if (this.mappingConfig) {
      this.buildFieldsList();
    }
    
    this.logger.info('map-configuration', 'Dati ricaricati');
  }

  /**
   * Costruisce la lista di campi dalla configurazione
   */
  buildFieldsList(): void {
    if (!this.mappingConfig || !this.mappingConfig.fields) {
      this.logger.warn('map-configuration', 'Nessuna configurazione disponibile');
      return;
    }

    this.mapFields = {};
    this.availableModels = [];
    const modelsSet = new Set<string>();

    Object.entries(this.mappingConfig.fields).forEach(([storageKey, config]) => {
      const scope = config.scope || 'other';
      const key = extractNameFromFieldConfigKey(storageKey);

      if (!this.mapFields[scope]) {
        this.mapFields[scope] = [];
      }

      // Arricchisci il config con i dati del catalog se disponibili
      const enrichedConfig = this.enrichFieldWithCatalog(key, config);
      enrichedConfig.scope = scope; // normalizza (config.scope potrebbe essere undefined su config legacy)

      this.mapFields[scope].push({
        key,
        config: enrichedConfig,
        visible: enrichedConfig.visible !== false
      });

      // Raccoglie modelli disponibili
      if (enrichedConfig.availableInModels && enrichedConfig.availableInModels.length > 0) {
        enrichedConfig.availableInModels.forEach(model => modelsSet.add(model));
      }
    });

    this.availableModels = Array.from(modelsSet).sort();
    this.loadScopeOrder();
    this.applyFilters();

    this.logger.debug('map-configuration', 'Fields list built', {
      scopes: Object.keys(this.mapFields).length,
      totalFields: this.getTotalFieldsCount()
    });
  }

  /**
   * Arricchisce un campo con le informazioni dal catalog_config
   */
  private enrichFieldWithCatalog(key: string, config: AssetFieldConfig): AssetFieldConfig {
    if (!this.catalogConfig || !this.catalogConfig[key]) {
      return config;
    }

    const catalogEntry = this.catalogConfig[key];
    const currentLanguage = localStorage.getItem('language') || 'en';
    
    // Formato chiave multilingua: "en-EN", "it-IT", "fr-FR", ecc.
    const languageKey = `${currentLanguage}-${currentLanguage.toUpperCase()}`;

    // Crea una copia del config per non modificare l'originale
    const enriched = { ...config };

    // Arricchisci con label multilingua se disponibile dal catalog
    if (catalogEntry.i18nLabel && catalogEntry.i18nLabel[languageKey]) {
      enriched.label = catalogEntry.i18nLabel[languageKey];
      this.logger.debug('map-configuration', `Label da catalog i18n per ${key}:`, enriched.label);
    } else if (catalogEntry.label) {
      enriched.label = catalogEntry.label;
      this.logger.debug('map-configuration', `Label da catalog (fallback) per ${key}:`, enriched.label);
    }

    // Aggiorna visibility dal catalog se specificato
    if (catalogEntry.visible !== undefined) {
      enriched.visible = catalogEntry.visible;
    }

    // Aggiungi flag kpi se presente
    if (catalogEntry.kpi !== undefined) {
      (enriched as any).kpi = catalogEntry.kpi;
    }

    // Aggiungi altre informazioni utili dal catalog
    if (catalogEntry.unit) {
      enriched.unit = catalogEntry.unit;
    }

    this.logger.debug('map-configuration', `Campo ${key} arricchito con catalog`, {
      originalLabel: config.label,
      enrichedLabel: enriched.label,
      visible: enriched.visible
    });

    return enriched;
  }

  /**
   * Applica i filtri di ricerca e modello
   */
  applyFilters(): void {
    const query = this.searchQuery.toLowerCase().trim();
    const modelFilter = this.selectedModel;

    this.filteredMapFields = {};

    Object.entries(this.mapFields).forEach(([scope, fields]) => {
      const filteredFields = fields.filter(field => {
        // Filtro ricerca
        const matchesSearch = !query ||
          field.key.toLowerCase().includes(query) ||
          field.config.label.toLowerCase().includes(query);

        // Filtro modello
        const matchesModel = !modelFilter ||
          (field.config.availableInModels && field.config.availableInModels.includes(modelFilter));

        return matchesSearch && matchesModel;
      }).map(field => ({
        ...field,
        highlighted: query.length > 0 && (
          field.key.toLowerCase().includes(query) ||
          field.config.label.toLowerCase().includes(query)
        )
      }));

      if (filteredFields.length > 0) {
        this.filteredMapFields[scope] = filteredFields;
      }
    });
  }

  /**
   * Gestisce il cambio di ricerca
   */
  onSearchChange(value: string): void {
    this.applyFilters();
  }

  /**
   * Pulisce la ricerca
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilters();
  }

  /**
   * Gestisce il cambio di filtro modello
   */
  onModelFilterChange(value: string): void {
    this.applyFilters();
  }

  /**
   * Toggle visibilità campo
   */
  toggleFieldVisibility(fieldKey: string, scope: string): void {
    const field = getFieldConfig(this.mappingConfig, scope, fieldKey);
    if (!field) return;

    field.visible = !field.visible;

    this.buildFieldsList();
    this.logger.debug('map-configuration', `Toggle visibility for ${fieldKey}: ${field.visible}`);
  }

  /**
   * Toggle destinazione campo
   */
  toggleFieldDestination(fieldKey: string, scope: string, destination: 'popup' | 'sidebar' | 'details', checked: boolean): void {
    const config = getFieldConfig(this.mappingConfig, scope, fieldKey);
    if (!config) return;

    switch (destination) {
      case 'popup':
        config.showInPopup = checked;
        break;
      case 'sidebar':
        config.showInSidebar = checked;
        break;
      case 'details':
        config.showInDetails = checked;
        break;
    }

    this.logger.debug('map-configuration', `Toggle destination ${destination} for ${fieldKey}: ${checked}`);
  }

  /**
   * Verifica se un campo ha una destinazione
   */
  getFieldDestination(fieldKey: string, scope: string, destination: 'popup' | 'sidebar' | 'details'): boolean {
    const config = getFieldConfig(this.mappingConfig, scope, fieldKey);
    if (!config) return false;

    switch (destination) {
      case 'popup':
        return config.showInPopup === true;
      case 'sidebar':
        return config.showInSidebar !== false;
      case 'details':
        return config.showInDetails !== false;
      default:
        return false;
    }
  }

  /**
   * Gestisce il click sulla checkbox popup con warning per limite
   */
  onPopupCheckboxClick(event: MouseEvent, fieldKey: string, scope: string): void {
    event.preventDefault();
    event.stopPropagation();

    const currentValue = this.getFieldDestination(fieldKey, scope, 'popup');
    const popupCount = this.getPopupFieldsCount();

    if (!currentValue && popupCount >= MapConfigurationComponent.MAX_POPUP_FIELDS) {
      this.dialog.open(WarningModalComponent, {
        width: '400px',
        data: {
          title: this.translateService.instant('confSignalsPanel.popupLimitTitle'),
          message: this.translateService.instant('confSignalsPanel.popupLimitMessage', {
            max: MapConfigurationComponent.MAX_POPUP_FIELDS
          })
        }
      });
      return;
    }

    this.toggleFieldDestination(fieldKey, scope, 'popup', !currentValue);
  }

  /**
   * Conta i campi con destinazione popup
   */
  private getPopupFieldsCount(): number {
    if (!this.mappingConfig) return 0;
    return Object.values(this.mappingConfig.fields)
      .filter(field => field.visible && field.showInPopup === true).length;
  }

  /**
   * Toggle tutti i campi sidebar in uno scope
   */
  toggleAllSidebar(scope: string, checked: boolean): void {
    if (!this.filteredMapFields[scope]) return;

    this.filteredMapFields[scope].forEach(field => {
      if (field.visible) {
        this.toggleFieldDestination(field.key, scope, 'sidebar', checked);
      }
    });
  }

  /**
   * Toggle tutti i campi details in uno scope
   */
  toggleAllDetails(scope: string, checked: boolean): void {
    if (!this.filteredMapFields[scope]) return;

    this.filteredMapFields[scope].forEach(field => {
      if (field.visible) {
        this.toggleFieldDestination(field.key, scope, 'details', checked);
      }
    });
  }

  /**
   * Verifica se tutti i sidebar sono selezionati
   */
  areAllSidebarChecked(scope: string): boolean {
    if (!this.filteredMapFields[scope]) return false;
    const visibleFields = this.filteredMapFields[scope].filter(f => f.visible);
    if (visibleFields.length === 0) return false;
    return visibleFields.every(f => this.getFieldDestination(f.key, scope, 'sidebar'));
  }

  /**
   * Verifica se tutti i details sono selezionati
   */
  areAllDetailsChecked(scope: string): boolean {
    if (!this.filteredMapFields[scope]) return false;
    const visibleFields = this.filteredMapFields[scope].filter(f => f.visible);
    if (visibleFields.length === 0) return false;
    return visibleFields.every(f => this.getFieldDestination(f.key, scope, 'details'));
  }

  /**
   * Verifica stato indeterminato sidebar
   */
  isSidebarIndeterminate(scope: string): boolean {
    if (!this.filteredMapFields[scope]) return false;
    const visibleFields = this.filteredMapFields[scope].filter(f => f.visible);
    if (visibleFields.length === 0) return false;
    const checkedCount = visibleFields.filter(f => this.getFieldDestination(f.key, scope, 'sidebar')).length;
    return checkedCount > 0 && checkedCount < visibleFields.length;
  }

  /**
   * Verifica stato indeterminato details
   */
  isDetailsIndeterminate(scope: string): boolean {
    if (!this.filteredMapFields[scope]) return false;
    const visibleFields = this.filteredMapFields[scope].filter(f => f.visible);
    if (visibleFields.length === 0) return false;
    const checkedCount = visibleFields.filter(f => this.getFieldDestination(f.key, scope, 'details')).length;
    return checkedCount > 0 && checkedCount < visibleFields.length;
  }

  /**
   * Toggle scope (tutti i campi visibili/invisibili)
   */
  onScopeToggleChange(scope: string): void {
    if (!this.filteredMapFields[scope]) return;

    const allEnabled = this.isScopeEnabled(scope);
    this.filteredMapFields[scope].forEach(field => {
      const config = getFieldConfig(this.mappingConfig, scope, field.key);
      if (config) {
        config.visible = !allEnabled;
      }
    });

    this.buildFieldsList();
  }

  /**
   * Verifica se uno scope è abilitato
   */
  isScopeEnabled(scope: string): boolean {
    if (!this.filteredMapFields[scope]) return false;
    return this.filteredMapFields[scope].every(f => f.visible);
  }

  /**
   * Verifica stato indeterminato scope
   */
  isScopeIndeterminate(scope: string): boolean {
    if (!this.filteredMapFields[scope]) return false;
    const visibleCount = this.filteredMapFields[scope].filter(f => f.visible).length;
    return visibleCount > 0 && visibleCount < this.filteredMapFields[scope].length;
  }

  /**
   * Drag & drop scope
   */
  onScopeDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.scopeOrder, event.previousIndex, event.currentIndex);
      this.saveScopeOrder();
      this.logger.debug('map-configuration', `Scope reordered:`, this.scopeOrder);
    }
  }

  /**
   * Drag & drop campi
   */
  onFieldDrop(event: CdkDragDrop<any[]>, scope: string): void {
    if (event.previousIndex !== event.currentIndex && this.mappingConfig) {
      const fields = this.filteredMapFields[scope];
      moveItemInArray(fields, event.previousIndex, event.currentIndex);

      // Aggiorna displayOrder nella configurazione
      fields.forEach((field, index) => {
        const config = getFieldConfig(this.mappingConfig, scope, field.key);
        if (config) {
          config.displayOrder = index;
        }
      });

      this.logger.debug('map-configuration', `Fields reordered in scope ${scope}`);
    }
  }

  /**
   * Toggle accordion gruppo
   */
  toggleGroup(scope: string): void {
    this.expandedGroups[scope] = !this.expandedGroups[scope];
  }

  /**
   * Verifica se un gruppo è espanso
   */
  isGroupExpanded(scope: string): boolean {
    return this.expandedGroups[scope] === true; // default chiuso
  }

  /**
   * Espande tutti i gruppi
   */
  private expandAllGroups(): void {
    MAP_FIELD_SCOPES.forEach(scope => {
      this.expandedGroups[scope] = true;
    });
  }

  /**
   * Salva ordine scope
   */
  private saveScopeOrder(): void {
    try {
      localStorage.setItem('map-configuration-scope-order', JSON.stringify(this.scopeOrder));
    } catch (error) {
      this.logger.error('map-configuration', 'Errore nel salvataggio ordine scope:', error);
    }
  }

  /**
   * Carica ordine scope
   */
  private loadScopeOrder(): void {
    try {
      const saved = localStorage.getItem('map-configuration-scope-order');
      if (saved) {
        const savedOrder = JSON.parse(saved);
        const validScopes = savedOrder.filter((scope: string) =>
          Object.keys(this.mapFields).includes(scope)
        );
        const newScopes = Object.keys(this.mapFields).filter(scope => !validScopes.includes(scope));
        this.scopeOrder = [...validScopes, ...newScopes];
      } else {
        this.scopeOrder = Object.keys(this.mapFields).sort();
      }
    } catch (error) {
      this.logger.error('map-configuration', 'Errore nel caricamento ordine scope:', error);
      this.scopeOrder = Object.keys(this.mapFields).sort();
    }
  }

  /**
   * Ritorna l'icona per lo scope
   */
  getScopeIcon(scope: string): any {
    const iconMap: { [key: string]: any } = {
      'signal': this.faSignal,
      'tcu': this.faCogs,
      'platform': this.faDesktop,
      'erp': this.faBuilding,
      'common': this.faWrench,
      'model': this.faCogs,
      'other': this.faQuestionCircle,
      'general': this.faQuestionCircle
    };
    return iconMap[scope] || this.faQuestionCircle;
  }

  /**
   * Ritorna il label per lo scope
   */
  getScopeLabel(scope: string): string {
    const translationKey = `scopeNames.${scope}`;
    const translatedLabel = this.translateService.instant(translationKey);

    if (translatedLabel && translatedLabel !== translationKey) {
      return translatedLabel;
    }

    return scope.toUpperCase();
  }

  /**
   * Ritorna il label del campo
   */
  getFieldLabel(field: any): string {
    // Priorità 1: Label dal catalog (se arricchito con i18nLabel)
    if (this.catalogConfig && this.catalogConfig[field.key]) {
      const catalogEntry = this.catalogConfig[field.key];
      const currentLanguage = localStorage.getItem('language') || 'en';
      const languageKey = `${currentLanguage}-${currentLanguage.toUpperCase()}`;
      
      if (catalogEntry.i18nLabel && catalogEntry.i18nLabel[languageKey]) {
        return catalogEntry.i18nLabel[languageKey];
      }
      if (catalogEntry.label && catalogEntry.label !== field.key) {
        return catalogEntry.label;
      }
    }
    
    // Priorità 2: Label dal dizionario di traduzione
    const translationKey = `catalog.signals.${field.key}`;
    const translatedLabel = this.translateService.instant(translationKey);
    if (translatedLabel && translatedLabel !== translationKey) {
      return translatedLabel;
    }

    // Priorità 3: Label dalla configurazione o chiave
    return field.config.label || field.key;
  }

  /**
   * Verifica se un campo ha modelli disponibili
   */
  hasAvailableModels(field: any): boolean {
    return field.config.availableInModels && field.config.availableInModels.length > 0;
  }

  /**
   * Formatta tooltip modelli
   */
  formatModelsTooltip(field: any): string {
    const models = field.config.availableInModels;
    if (!models || models.length === 0) {
      return '';
    }

    const header = this.translateService.instant('confSignalsPanel.modelsTooltipHeader');
    const separator = '━'.repeat(25);
    const modelsList = models.map((model: string, index: number) =>
      `${index + 1}. ${model}`
    ).join('\n');
    const footer = this.translateService.instant('confSignalsPanel.modelsTooltipFooter');

    return `${header}\n${separator}\n\n${modelsList}\n\n${footer}`;
  }

  /**
   * Conta totale campi
   */
  getTotalFieldsCount(): number {
    return Object.values(this.mapFields).reduce((total, fields) => total + fields.length, 0);
  }

  /**
   * TrackBy per scope
   */
  trackByScope(_index: number, scope: string): string {
    return scope;
  }

  /**
   * TrackBy per field key
   */
  trackByFieldKey(_index: number, field: any): string {
    return field.key;
  }

  /**
   * Ricarica configurazione (chiamato dal retry button)
   */
  loadMappingConfiguration(): void {
    // Questo metodo sarà gestito dal parent component
    this.logger.debug('map-configuration', 'Ricarica configurazione richiesta');
  }
}
